import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { operator, type OperatorCustomerRow } from '../lib/api.js';

type SortKey =
  | 'name'
  | 'vertical'
  | 'created'
  | 'regressions'
  | 'backup'
  | 'content'
  | 'lighthouse';

export function OperatorCustomersPage() {
  const q = useQuery({
    queryKey: ['operator', 'customers'],
    queryFn: operator.customers,
    staleTime: 30_000,
  });

  const [sort, setSort] = useState<SortKey>('regressions');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');

  const rows = useMemo(() => {
    const list = q.data?.customers ?? [];
    return [...list].sort((a, b) => {
      const av = sortKey(a, sort);
      const bv = sortKey(b, sort);
      if (av === bv) return 0;
      const cmp = av < bv ? -1 : 1;
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [q.data, sort, dir]);

  const onSort = (key: SortKey): void => {
    if (sort === key) setDir(dir === 'asc' ? 'desc' : 'asc');
    else { setSort(key); setDir('desc'); }
  };

  const total = q.data?.customers.length ?? 0;
  const withRegressions = rows.filter((r) => r.qa_regressions_24h > 0).length;
  const withBackupFails = rows.filter((r) => r.last_db_backup_status === 'failed_run').length;

  return (
    <div className="space-y-12">
      <section>
        <p className="text-xs uppercase tracking-[0.28em] text-cream/40 mb-5">
          Operator Console
        </p>
        <h1 className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tightest text-cream">
          All customers
        </h1>
        <p className="mt-5 text-base text-cream/55 max-w-2xl">
          Every Bloom account, sorted by what needs your attention first. Click a
          row to drill into that customer.
        </p>
      </section>

      {/* PORTFOLIO QUICK STATS ─────────────────────────────────── */}
      <section>
        <div className="grid grid-cols-3 gap-4">
          <Big label="Customers" value={total} />
          <Big
            label="With regressions"
            value={withRegressions}
            tone={withRegressions > 0 ? 'warn' : 'ok'}
          />
          <Big
            label="Backup failures"
            value={withBackupFails}
            tone={withBackupFails > 0 ? 'urgent' : 'ok'}
          />
        </div>
      </section>

      {/* TABLE ──────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-[10px] uppercase tracking-[0.28em] text-cream/35 mb-5">
          Customer roster
        </h2>

        {q.isLoading && <div className="text-sm text-cream/40">Loading customers…</div>}
        {q.isError && (
          <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
            Couldn't load customer list. Refresh in a moment.
          </div>
        )}

        {q.data && rows.length === 0 && (
          <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-8 text-center text-cream/55 text-sm">
            No active customers yet.
          </div>
        )}

        {q.data && rows.length > 0 && (
          <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.18em] text-cream/35 border-b border-cream/[0.08]">
                  <Th sort={sort} dir={dir} onSort={onSort} sortKey="name" align="left">Customer</Th>
                  <Th sort={sort} dir={dir} onSort={onSort} sortKey="vertical" align="left">Vertical</Th>
                  <Th sort={sort} dir={dir} onSort={onSort} sortKey="regressions" align="right">Regressions 24h</Th>
                  <Th sort={sort} dir={dir} onSort={onSort} sortKey="backup" align="left">Last backup</Th>
                  <Th sort={sort} dir={dir} onSort={onSort} sortKey="content" align="right">Posts 7d</Th>
                  <Th sort={sort} dir={dir} onSort={onSort} sortKey="lighthouse" align="right">Perf</Th>
                  <Th sort={sort} dir={dir} onSort={onSort} sortKey="created" align="left">Onboarded</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.account_id}
                    className={`border-b border-cream/[0.05] hover:bg-cream/[0.02] transition-colors ${i === rows.length - 1 ? 'border-b-0' : ''}`}
                  >
                    <td className="px-5 py-4">
                      <div className="font-serif text-lg text-cream leading-tight">{r.display_name}</div>
                      <div className="mt-0.5 text-[11px] text-cream/40">
                        {r.city && r.state ? `${r.city}, ${r.state}` : r.status}
                        {r.primary_owner_email && (
                          <span className="text-cream/30"> · {r.primary_owner_email}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-cream/65 capitalize">{r.vertical}</td>
                    <td className="px-5 py-4 text-right">
                      {r.qa_regressions_24h > 0 ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/[0.1] text-amber-300 text-xs font-medium">
                          <span className="w-1 h-1 rounded-full bg-amber-300 animate-pulse" />
                          {r.qa_regressions_24h}
                        </span>
                      ) : (
                        <span className="text-cream/30">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <BackupCell row={r} />
                    </td>
                    <td className="px-5 py-4 text-right tabular-nums text-cream/75">
                      {r.content_published_7d > 0 ? r.content_published_7d : <span className="text-cream/30">—</span>}
                    </td>
                    <td className="px-5 py-4 text-right tabular-nums">
                      {r.last_lighthouse_perf == null ? (
                        <span className="text-cream/30">—</span>
                      ) : (
                        <PerfBadge score={r.last_lighthouse_perf} />
                      )}
                    </td>
                    <td className="px-5 py-4 text-cream/50 text-xs">
                      {formatRelativeDate(r.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function sortKey(r: OperatorCustomerRow, key: SortKey): string | number {
  switch (key) {
    case 'name':        return r.display_name.toLowerCase();
    case 'vertical':    return r.vertical;
    case 'created':     return r.created_at;
    case 'regressions': return r.qa_regressions_24h;
    case 'backup':      return r.last_db_backup_at ?? '0';
    case 'content':     return r.content_published_7d;
    case 'lighthouse':  return r.last_lighthouse_perf ?? -1;
  }
}

function Th({
  children, sort, dir, sortKey: thKey, onSort, align,
}: {
  children: React.ReactNode;
  sort: SortKey;
  dir: 'asc' | 'desc';
  sortKey: SortKey;
  onSort: (k: SortKey) => void;
  align: 'left' | 'right';
}) {
  const active = sort === thKey;
  return (
    <th className={`px-5 py-3 ${align === 'right' ? 'text-right' : 'text-left'} font-medium`}>
      <button
        onClick={() => onSort(thKey)}
        className={`inline-flex items-center gap-1 hover:text-cream transition-colors ${active ? 'text-cream' : ''}`}
      >
        {children}
        {active && (
          <span className="text-[8px]">{dir === 'asc' ? '↑' : '↓'}</span>
        )}
      </button>
    </th>
  );
}

function BackupCell({ row }: { row: OperatorCustomerRow }) {
  if (!row.last_db_backup_status) {
    return <span className="text-cream/30 text-xs">never run</span>;
  }
  const ok = row.last_db_backup_status === 'success';
  const noConfig = row.last_db_backup_status === 'failed_no_config';
  return (
    <div className="flex flex-col">
      <span
        className={`text-xs font-medium ${
          ok ? 'text-emerald-300/90'
          : noConfig ? 'text-cream/45'
          : 'text-red-400/90'
        }`}
      >
        {ok ? '✓ success' : noConfig ? '— no config' : '✗ failed'}
      </span>
      <span className="text-[10px] text-cream/40 mt-0.5">
        {formatRelativeDate(row.last_db_backup_at!)}
      </span>
    </div>
  );
}

function PerfBadge({ score }: { score: number }) {
  const tone =
    score >= 90 ? 'text-emerald-300'
    : score >= 75 ? 'text-cream'
    : score >= 50 ? 'text-amber-300'
    : 'text-red-400';
  return <span className={`font-serif text-lg ${tone}`}>{score}</span>;
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffMs < 0) return 'in future';
  if (diffMs < 60_000) return 'just now';
  if (diffMs < 60 * 60_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  if (diffMs < 24 * 60 * 60_000) return `${Math.floor(diffMs / (60 * 60_000))}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function Big({
  label, value, tone = 'neutral',
}: {
  label: string;
  value: number;
  tone?: 'ok' | 'warn' | 'urgent' | 'neutral';
}) {
  const numClass =
    tone === 'urgent' ? 'text-red-400'
    : tone === 'warn' ? 'text-amber-300'
    : tone === 'ok' && value > 0 ? 'text-emerald-300'
    : 'text-cream';
  return (
    <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6">
      <div className="text-[10px] uppercase tracking-[0.24em] text-cream/40">{label}</div>
      <div className={`mt-2 font-serif text-5xl tabular-nums leading-none tracking-tightest ${numClass}`}>{value}</div>
    </div>
  );
}
